(() => {
  const BASE = 'PKR';
  const API = 'https://v6.exchangerate-api.com/v6/2bb60105474e86affa3f43a2/latest/PKR';
  const FALLBACK_API = 'https://open.er-api.com/v6/latest/PKR';
  const PREF_KEY = 'ebonform_currency_preference_v1';
  const RATE_KEY = 'ebonform_currency_rates_v2';
  const CACHE_MS = 60 * 60 * 1000;
  const currencies = {
    PKR:{country:'PK',flag:'🇵🇰',locale:'en-PK'},
    USD:{country:'US',flag:'🇺🇸',locale:'en-US'},
    AUD:{country:'AU',flag:'🇦🇺',locale:'en-AU'},
    CNY:{country:'CN',flag:'🇨🇳',locale:'zh-CN'},
    JPY:{country:'JP',flag:'🇯🇵',locale:'ja-JP'},
    INR:{country:'IN',flag:'🇮🇳',locale:'en-IN'},
    NZD:{country:'NZ',flag:'🇳🇿',locale:'en-NZ'},
    AED:{country:'AE',flag:'🇦🇪',locale:'en-AE'},
    SAR:{country:'SA',flag:'🇸🇦',locale:'en-SA'}
  };

  const roots = () => [...document.querySelectorAll('[data-efcurrency]')];
  const knownNodes = new Set();
  const baseText = new WeakMap();
  let selected = BASE;
  let rates = { PKR: 1 };
  let internalWrite = false;

  const readPreference = () => {
    try {
      const value = localStorage.getItem(PREF_KEY);
      if (value && currencies[value]) return value;
    } catch (error) {}
    return BASE;
  };

  const savePreference = value => {
    try { localStorage.setItem(PREF_KEY, value); } catch (error) {}
  };

  const hasBaseMoney = value => /(?:(?:PKR|Rs\.?|₨)\s*\d[\d,]*(?:\.\d+)?(?:\s*PKR)?|\d[\d,]*(?:\.\d+)?\s*PKR)/i.test(value || '');

  const amount = raw => Number(String(raw).replace(/,/g, ''));

  const formatMoney = (value, code) => {
    const fractionDigits = code === 'JPY' ? 0 : 2;
    try {
      return new Intl.NumberFormat(currencies[code]?.locale || 'en-US', {
        style: 'currency',
        currency: code,
        currencyDisplay: 'code',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
      }).format(value).replace(/\u00a0/g, ' ');
    } catch (error) {
      return code + ' ' + value.toFixed(fractionDigits);
    }
  };

  const convertText = original => {
    if (selected === BASE) return original;
    const rate = Number(rates[selected]);
    if (!rate) return original;

    return original.replace(
      /(?:(?:PKR|Rs\.?|₨)\s*(\d[\d,]*(?:\.\d+)?)(?:\s*PKR)?|(\d[\d,]*(?:\.\d+)?)\s*PKR)/gi,
      (match, prefixed, suffixed) => {
        const value = amount(prefixed || suffixed);
        return Number.isFinite(value) ? formatMoney(value * rate, selected) : match;
      }
    );
  };

  const applyNode = node => {
    if (!node || !node.parentElement || node.parentElement.closest('[data-efcurrency],script,style,noscript,textarea')) return;
    const current = node.nodeValue || '';

    if (hasBaseMoney(current)) {
      baseText.set(node, current);
      knownNodes.add(node);
    }

    const original = baseText.get(node);
    if (!original) return;

    const next = selected === BASE ? original : convertText(original);
    if (node.nodeValue !== next) {
      internalWrite = true;
      node.nodeValue = next;
      internalWrite = false;
    }
  };

  const scan = root => {
    if (!root) return;
    if (root.nodeType === Node.TEXT_NODE) {
      applyNode(root);
      return;
    }
    if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE && root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return;
    if (root.nodeType === Node.ELEMENT_NODE && root.matches('[data-efcurrency],script,style,noscript,textarea')) return;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) applyNode(node);
  };

  const applyAll = () => {
    knownNodes.forEach(node => {
      if (!node.isConnected) {
        knownNodes.delete(node);
        return;
      }
      applyNode(node);
    });
    document.documentElement.dataset.ebonCurrency = selected;
  };

  const status = (message, state) => {
    roots().forEach(root => {
      const el = root.querySelector('[data-efcurrency-status]');
      if (!el) return;
      if (!message) {
        el.hidden = true;
        el.textContent = '';
        el.removeAttribute('data-state');
        return;
      }
      el.hidden = false;
      el.textContent = message;
      if (state) el.dataset.state = state;
      else el.removeAttribute('data-state');
    });
  };

  const syncControls = () => {
    const meta = currencies[selected] || currencies.PKR;
    roots().forEach(root => {
      root.querySelector('[data-efcurrency-flag]')?.replaceChildren(document.createTextNode(meta.flag));
      root.querySelector('[data-efcurrency-code]')?.replaceChildren(document.createTextNode(selected));
      root.querySelectorAll('[data-efcurrency-option]').forEach(option => {
        const active = option.dataset.currency === selected;
        option.classList.toggle('is-selected', active);
        option.setAttribute('aria-selected', String(active));
      });
    });
  };

  const closeAll = except => {
    roots().forEach(root => {
      if (root === except) return;
      root.classList.remove('is-open');
      root.querySelector('[data-efcurrency-panel]')?.setAttribute('hidden', '');
      root.querySelector('[data-efcurrency-toggle]')?.setAttribute('aria-expanded', 'false');
    });
  };

  const cachedRates = () => {
    try {
      const cache = JSON.parse(localStorage.getItem(RATE_KEY) || 'null');
      if (!cache?.rates || !cache.fetchedAt) return null;
      return cache;
    } catch (error) {
      return null;
    }
  };

  const requestRates = async url => {
    const response = await fetch(url, { mode: 'cors', credentials: 'omit', cache: 'no-store' });
    if (!response.ok) throw new Error('FX request failed');

    const data = await response.json();
    const nextRates = data.conversion_rates || data.rates;

    if (data.result !== 'success' || !nextRates) {
      throw new Error(data['error-type'] || 'FX response invalid');
    }

    return {
      rates: nextRates,
      updated: data.time_last_update_utc || '',
      nextUpdate: data.time_next_update_utc || ''
    };
  };

  const getRates = async () => {
    const cache = cachedRates();
    if (cache?.rates && Date.now() - cache.fetchedAt < CACHE_MS) {
      rates = cache.rates;
      return { cached: true, updated: cache.updated || '' };
    }

    let result;

    try {
      result = await requestRates(API);
    } catch (authenticatedError) {
      try {
        result = await requestRates(FALLBACK_API);
      } catch (fallbackError) {
        if (cache?.rates) {
          rates = cache.rates;
          return { cached: true, stale: true, updated: cache.updated || '' };
        }
        throw authenticatedError;
      }
    }

    rates = result.rates;
    const payload = {
      rates,
      fetchedAt: Date.now(),
      updated: result.updated || '',
      nextUpdate: result.nextUpdate || ''
    };

    try { localStorage.setItem(RATE_KEY, JSON.stringify(payload)); } catch (error) {}
    return { cached: false, updated: payload.updated };
  };

  const choose = async (code, labels) => {
    if (!currencies[code]) return;
    selected = code;
    savePreference(selected);
    syncControls();
    closeAll();

    if (selected === BASE) {
      status('');
      applyAll();
      document.dispatchEvent(new CustomEvent('ebonform:currency-changed', { detail: { currency: selected, rate: 1 } }));
      return;
    }

    status(labels.loading || '');
    try {
      await getRates();
      if (!Number(rates[selected])) throw new Error('Selected FX rate unavailable');
      applyAll();
      status(labels.live || '');
      document.dispatchEvent(new CustomEvent('ebonform:currency-changed', { detail: { currency: selected, rate: rates[selected] } }));
    } catch (error) {
      selected = BASE;
      savePreference(selected);
      syncControls();
      applyAll();
      status(labels.error || '', 'error');
    }
  };

  const initRoot = root => {
    if (root.dataset.efcurrencyReady === 'true') return;
    root.dataset.efcurrencyReady = 'true';

    const toggle = root.querySelector('[data-efcurrency-toggle]');
    const panel = root.querySelector('[data-efcurrency-panel]');
    const labels = {
      loading: root.dataset.loading || document.documentElement.dataset.efcurrencyLoading || '',
      live: root.dataset.live || document.documentElement.dataset.efcurrencyLive || '',
      error: root.dataset.error || document.documentElement.dataset.efcurrencyError || ''
    };

    toggle?.addEventListener('click', event => {
      event.stopPropagation();
      const opening = !root.classList.contains('is-open');
      closeAll(opening ? root : null);
      root.classList.toggle('is-open', opening);
      if (panel) panel.hidden = !opening;
      toggle.setAttribute('aria-expanded', String(opening));
    });

    root.querySelectorAll('[data-efcurrency-option]').forEach(option => {
      option.addEventListener('click', () => choose(option.dataset.currency, labels));
    });
  };

  const init = () => {
    selected = readPreference();

    roots().forEach(root => {
      root.dataset.loading = root.dataset.loading || '';
      root.dataset.live = root.dataset.live || '';
      root.dataset.error = root.dataset.error || '';
      initRoot(root);
    });

    syncControls();
    scan(document.body);

    if (selected !== BASE) {
      const anyRoot = roots()[0];
      const labels = {
        loading: document.documentElement.dataset.efcurrencyLoading || '',
        live: document.documentElement.dataset.efcurrencyLive || '',
        error: document.documentElement.dataset.efcurrencyError || ''
      };
      choose(selected, labels);
    }

    const observer = new MutationObserver(mutations => {
      if (internalWrite) return;
      mutations.forEach(mutation => {
        if (mutation.type === 'characterData') {
          applyNode(mutation.target);
          return;
        }
        mutation.addedNodes.forEach(node => scan(node));
      });
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    document.addEventListener('click', event => {
      if (!event.target.closest('[data-efcurrency]')) closeAll();
    });
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape') closeAll();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();